import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { fmtFP, fmtUSD } from '../../lib/money';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import {
  Building2, Plus, Edit2, Trash2, Globe, Copy, Users, Upload, Image,
  ChevronDown, ChevronRight, Shield, Key, Ban, Check, BarChart3,
  Wallet, Gamepad2, ArrowDownCircle, ArrowUpCircle, Eye, EyeOff,
  RefreshCw, Search, Link2, DollarSign, Palette
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PLATFORM_DOMAIN = (BACKEND_URL || window.location.origin).replace('https://', '').replace('http://', '');

const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);

export default function TenantsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const adminBase = tenantSlug ? `/${tenantSlug}/admin` : '/admin';
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [tenantPlayers, setTenantPlayers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPlayers, setShowPlayers] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [aggregate, setAggregate] = useState(null);
  const [showSlugModal, setShowSlugModal] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [showResetPw, setShowResetPw] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const logoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '', slug: '', description: '',
    branding: { primary_color: '#D4AF37', secondary_color: '#10B981', logo_url: '' }
  });
  const [adminData, setAdminData] = useState({ tenant_id: '', email: '', full_name: '', password: '' });

  useEffect(() => { fetchTenants(); fetchAggregate(); }, []);

  const fetchTenants = async () => {
    try {
      const res = await api.get('/api/admin/tenants');
      setTenants(res.data.items || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchAggregate = async () => {
    try {
      const res = await api.get('/api/admin/tenants/aggregate/overview');
      setAggregate(res.data);
    } catch {}
  };

  const fetchTenantDetail = async (tenantId) => {
    setDetailLoading(true);
    try {
      const [overRes, admRes] = await Promise.all([
        api.get(`/api/admin/tenants/${tenantId}/overview`),
        api.get(`/api/admin/tenants/${tenantId}/admins`),
      ]);
      setTenantDetail(overRes.data);
      setTenantAdmins(admRes.data.admins || []);
      setTenantPlayers([]);
      setShowPlayers(false);
    } catch (err) { toast.error('Failed to load tenant details'); }
    finally { setDetailLoading(false); }
  };

  const fetchTenantPlayers = async (tenantId) => {
    setPlayersLoading(true);
    try {
      const res = await api.get(`/api/admin/tenants/${tenantId}/players`);
      setTenantPlayers(res.data.players || []);
      setShowPlayers(true);
    } catch { toast.error('Failed to load players'); }
    finally { setPlayersLoading(false); }
  };

  const toggleExpand = (tenantId) => {
    if (expandedTenant === tenantId) {
      setExpandedTenant(null);
      setTenantDetail(null);
    } else {
      setExpandedTenant(tenantId);
      fetchTenantDetail(tenantId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { name: formData.name, slug: formData.slug, status: 'active', branding: formData.branding, settings: {} };
      if (editing) {
        await api.put(`/api/admin/tenants/${editing.id}`, payload);
        toast.success('Tenant updated');
      } else {
        const res = await api.post('/api/admin/tenants', payload);
        const newTenant = res.data.tenant;
        // Upload logo if selected
        if (logoInputRef.current?.files?.[0] && newTenant) {
          await handleLogoUpload(newTenant.id, logoInputRef.current.files[0]);
        }
        toast.success('Tenant created');
      }
      setShowForm(false);
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/admin/tenants/create-admin', adminData);
      toast.success('Tenant admin created');
      setShowAdminForm(false);
      setAdminData({ tenant_id: '', email: '', full_name: '', password: '' });
      if (expandedTenant === adminData.tenant_id) fetchTenantDetail(adminData.tenant_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tenant and all associated data?')) return;
    try {
      await api.delete(`/api/admin/tenants/${id}`);
      toast.success('Tenant deleted');
      fetchTenants();
    } catch { toast.error('Delete failed'); }
  };

  const handleLogoUpload = async (tenantId, file) => {
    if (!file) return;
    setUploadingLogo(tenantId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/admin/tenants/${tenantId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded');
      fetchTenants();
    } catch { toast.error('Upload failed'); }
    finally { setUploadingLogo(null); }
  };

  const handleToggleStatus = async (t) => {
    const newStatus = t.status === 'active' ? 'disabled' : 'active';
    try {
      await api.patch(`/api/admin/tenants/${t.id}/status`, { status: newStatus });
      toast.success(`Tenant ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      fetchTenants();
    } catch { toast.error('Failed'); }
  };

  const handleSlugChange = async () => {
    if (!showSlugModal || !newSlug) return;
    try {
      await api.patch(`/api/admin/tenants/${showSlugModal}/slug`, { new_slug: newSlug });
      toast.success('Tenant URL updated');
      setShowSlugModal(null);
      setNewSlug('');
      fetchTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async () => {
    if (!showResetPw || !resetPw) return;
    try {
      await api.post(`/api/admin/tenants/${showResetPw.tenant_id}/admins/${showResetPw.id}/reset-password`, { new_password: resetPw });
      toast.success('Password reset');
      setShowResetPw(null);
      setResetPw('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleAdmin = async (tenantId, adminId) => {
    try {
      await api.patch(`/api/admin/tenants/${tenantId}/admins/${adminId}/toggle`);
      toast.success('Admin status toggled');
      fetchTenantDetail(tenantId);
    } catch { toast.error('Failed'); }
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '', branding: { primary_color: '#D4AF37', secondary_color: '#10B981', logo_url: '' } });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const openEdit = (t) => {
    setEditing(t);
    setFormData({ name: t.name, slug: t.slug, description: t.description || '', branding: t.branding || { primary_color: '#D4AF37', secondary_color: '#10B981', logo_url: '' } });
    setShowForm(true);
  };

  const getLogoUrl = (t) => {
    const url = t.branding?.logo_url;
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const copyUrl = (slug) => { navigator.clipboard.writeText(`https://${PLATFORM_DOMAIN}/${slug}`); toast.success('URL copied!'); };
  const filtered = tenants.filter(t =>
    !searchQuery || t.name?.toLowerCase().includes(searchQuery.toLowerCase()) || t.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Building2 className="w-5 h-5 text-[#D4AF37]" /> Tenants</h2>
          <p className="text-xs text-gray-400 mt-0.5">Manage tenant groups, admins, players, and analytics</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setShowAdminForm(true); }} variant="outline" className="border-white/10 text-sm" data-testid="create-admin-btn">
            <Shield className="w-4 h-4 mr-1.5" /> Create Admin
          </Button>
          <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="bg-[#D4AF37] text-black text-sm" data-testid="add-tenant-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Tenant
          </Button>
        </div>
      </div>

      {/* Aggregate Stats */}
      {aggregate && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="aggregate-stats">
          <AggCard label="Total Tenants" value={aggregate.aggregate?.total_tenants} color="#D4AF37" icon={Building2} />
          <AggCard label="Total Players" value={aggregate.aggregate?.total_players} color="#3B82F6" icon={Users} />
          <AggCard label="Total Deposits" value={fmtUSD(aggregate.aggregate?.total_deposits)} color="#22c55e" icon={ArrowDownCircle} />
          <AggCard label="Total Withdrawals" value={fmtUSD(aggregate.aggregate?.total_withdrawals)} color="#ef4444" icon={ArrowUpCircle} />
          <AggCard label="Wallet Cash" value={fmtUSD(aggregate.aggregate?.total_wallet_balance)} color="#8b5cf6" icon={Wallet} />
          <AggCard label="Game Loads" value={fmtUSD(aggregate.aggregate?.total_game_loads)} color="#f59e0b" icon={Gamepad2} />
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tenants..." className="bg-[#131822] border-white/10 pl-10" data-testid="tenant-search" />
      </div>

      {/* Tenant List */}
      <div className="space-y-3">
        {filtered.map((t) => (
          <div key={t.id} className="bg-[#131822] rounded-xl border border-white/10 overflow-hidden" data-testid={`tenant-card-${t.slug}`}>
            {/* Tenant Header Row */}
            <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-white/[0.02] transition" onClick={() => toggleExpand(t.id)}>
              {getLogoUrl(t) ? (
                <img src={getLogoUrl(t)} alt={t.name} className="w-11 h-11 rounded-xl object-cover border border-white/10" />
              ) : (
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-lg font-bold" style={{ background: `${t.branding?.primary_color || '#D4AF37'}20`, color: t.branding?.primary_color || '#D4AF37' }}>
                  {t.name?.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-white truncate">{t.name}</h3>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${t.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{t.status || 'active'}</span>
                </div>
                <p className="text-xs text-gray-500 font-mono">/{t.slug}</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.player_count || 0}</span>
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> {t.admin_count || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={(e) => { e.stopPropagation(); copyUrl(t.slug); }} className="p-1.5 rounded hover:bg-white/5 text-gray-400" title="Copy URL"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); setShowSlugModal(t.id); setNewSlug(t.slug); }} className="p-1.5 rounded hover:bg-white/5 text-gray-400" title="Change URL"><Link2 className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); handleToggleStatus(t); }} className={`p-1.5 rounded hover:bg-white/5 ${t.status === 'active' ? 'text-green-400' : 'text-red-400'}`} title={t.status === 'active' ? 'Disable' : 'Enable'}>
                  {t.status === 'active' ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); openEdit(t); }} className="p-1.5 rounded hover:bg-white/5 text-gray-400" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); navigate(`${adminBase}/tenant-theme/${t.id}`); }} className="p-1.5 rounded hover:bg-white/5 text-emerald-400" title="Custom Theme" data-testid={`theme-btn-${t.slug}`}><Palette className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }} className="p-1.5 rounded hover:bg-white/5 text-red-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                {expandedTenant === t.id ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
              </div>
            </div>

            {/* Expanded Detail */}
            {expandedTenant === t.id && (
              <div className="border-t border-white/5 bg-[#0d1117]">
                {detailLoading ? (
                  <div className="flex justify-center py-8"><div className="spinner" /></div>
                ) : tenantDetail ? (
                  <div className="p-4 space-y-4">
                    {/* URLs */}
                    <div className="bg-[#131822] rounded-lg p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Player URL</span>
                        <div className="flex items-center gap-1">
                          <a href={`/${t.slug}/auth`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D4AF37] hover:underline font-mono">{PLATFORM_DOMAIN}/{t.slug}</a>
                          <button onClick={() => copyUrl(t.slug)} className="p-0.5"><Copy className="w-3 h-3 text-gray-500" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 flex items-center gap-1.5"><Shield className="w-3 h-3" /> Admin URL</span>
                        <span className="text-xs text-gray-500 font-mono">{PLATFORM_DOMAIN}/{t.slug}/admin</span>
                      </div>
                    </div>

                    {/* Summary Metric Cards — clickable */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button onClick={() => showPlayers ? setShowPlayers(false) : fetchTenantPlayers(t.id)} className="bg-[#0B0E14] rounded-lg p-3 text-center hover:ring-1 hover:ring-blue-500/30 transition cursor-pointer" data-testid="tenant-players-btn">
                        <p className="text-lg font-bold text-blue-400">{tenantDetail.metrics?.players || 0}</p>
                        <p className="text-[10px] text-gray-500">Players {showPlayers ? '(hide)' : '(view)'}</p>
                      </button>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-purple-400">{tenantDetail.metrics?.tenant_admins || 0}</p>
                        <p className="text-[10px] text-gray-500">Admins</p>
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-[#D4AF37]">{tenantDetail.metrics?.game_accounts || 0}</p>
                        <p className="text-[10px] text-gray-500">Game Accounts</p>
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-green-400">{fmtUSD(tenantDetail.metrics?.wallets?.total_cash)}</p>
                        <p className="text-[10px] text-gray-500">Wallet Cash</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-sm font-bold text-blue-400">{fmtFP(tenantDetail.metrics?.wallets?.total_play)}</p>
                        <p className="text-[10px] text-gray-500">FP Credits</p>
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-sm font-bold text-green-400">{fmtUSD(tenantDetail.metrics?.deposits?.total)} ({tenantDetail.metrics?.deposits?.count || 0})</p>
                        <p className="text-[10px] text-gray-500">Deposits</p>
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-sm font-bold text-red-400">{fmtUSD(tenantDetail.metrics?.withdrawals?.total)} ({tenantDetail.metrics?.withdrawals?.count || 0})</p>
                        <p className="text-[10px] text-gray-500">Withdrawals</p>
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3 text-center">
                        <p className="text-sm font-bold text-yellow-400">{fmtUSD(tenantDetail.metrics?.game_loads?.total)} ({tenantDetail.metrics?.game_loads?.count || 0})</p>
                        <p className="text-[10px] text-gray-500">Game Loads</p>
                      </div>
                    </div>

                    {/* Admins — 3 per row grid */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#D4AF37]" /> Tenant Admins</h4>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-white/10"
                          onClick={() => { setAdminData(d => ({ ...d, tenant_id: t.id })); setShowAdminForm(true); }}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      {tenantAdmins.length === 0 ? (
                        <p className="text-xs text-gray-500 py-2">No admins yet</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {tenantAdmins.map(a => (
                            <div key={a.id} className="flex items-center justify-between bg-[#131822] rounded-lg px-3 py-2.5">
                              <div className="min-w-0">
                                <p className="text-sm text-white truncate">{a.full_name || a.username}</p>
                                <p className="text-[10px] text-gray-500 truncate">{a.email}</p>
                                <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${a.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                  {a.disabled ? 'Disabled' : 'Active'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                <button onClick={() => { setShowResetPw({ id: a.id, tenant_id: t.id, email: a.email }); setResetPw(''); }} className="p-1.5 rounded hover:bg-white/5 text-yellow-400" title="Reset Password"><Key className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleToggleAdmin(t.id, a.id)} className={`p-1.5 rounded hover:bg-white/5 ${a.disabled ? 'text-green-400' : 'text-red-400'}`} title={a.disabled ? 'Enable' : 'Disable'}>
                                  {a.disabled ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Players — only shown on click */}
                    {playersLoading && <div className="flex justify-center py-4"><div className="spinner" /></div>}
                    {showPlayers && !playersLoading && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><Users className="w-4 h-4 text-[#3B82F6]" /> Players ({tenantPlayers.length})</h4>
                          <button onClick={() => setShowPlayers(false)} className="text-xs text-gray-500 hover:text-white">Hide</button>
                        </div>
                        {tenantPlayers.length === 0 ? (
                          <p className="text-xs text-gray-500 py-2">No players yet</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 border-b border-white/5">
                                  <th className="text-left py-2 px-2 font-medium">Player</th>
                                  <th className="text-left py-2 px-2 font-medium">Email</th>
                                  <th className="text-right py-2 px-2 font-medium">Cash</th>
                                  <th className="text-right py-2 px-2 font-medium">FP</th>
                                  <th className="text-left py-2 px-2 font-medium">Tier</th>
                                  <th className="text-left py-2 px-2 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tenantPlayers.slice(0, 50).map(p => (
                                  <tr key={p.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                                    <td className="py-1.5 px-2 text-white">{p.username || p.full_name}</td>
                                    <td className="py-1.5 px-2 text-gray-400">{p.email}</td>
                                    <td className="py-1.5 px-2 text-right text-green-400">{fmtUSD(p.cash_balance)}</td>
                                    <td className="py-1.5 px-2 text-right text-blue-400">{fmtFP(p.play_credits)}</td>
                                    <td className="py-1.5 px-2 capitalize text-[#D4AF37]">{p.achievement_tier || 'Bronze'}</td>
                                    <td className="py-1.5 px-2">
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${p.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                        {p.disabled ? 'Disabled' : 'Active'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {tenantPlayers.length > 50 && <p className="text-[10px] text-gray-500 mt-1 text-center">Showing 50 of {tenantPlayers.length}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>

      {filtered.length === 0 && !searchQuery && (
        <div className="text-center py-12 bg-[#131822] rounded-xl border border-white/10">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400">No tenants yet. Create your first tenant group.</p>
        </div>
      )}

      {/* Create/Edit Tenant Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Tenant' : 'Create Tenant'} maxWidth="max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Tenant Name</label>
            <Input value={formData.name} onChange={(e) => { const n = e.target.value; setFormData(p => ({ ...p, name: n, slug: editing ? p.slug : autoSlug(n) })); }}
              required className="bg-[#0B0E14] border-white/10" placeholder="e.g., Team Titan" data-testid="tenant-name" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Tenant URL</label>
            <div className="flex items-center bg-[#0B0E14] border border-white/10 rounded-lg overflow-hidden">
              <span className="px-3 py-2.5 text-xs text-gray-500 bg-[#0a0d12] border-r border-white/10 whitespace-nowrap">{PLATFORM_DOMAIN}/</span>
              <input value={formData.slug} onChange={(e) => setFormData(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
                className="flex-1 px-3 py-2.5 bg-transparent text-sm text-white font-mono outline-none" placeholder="slug" data-testid="tenant-slug" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Description</label>
            <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-[#0B0E14] border-white/10" placeholder="Brief description..." />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Logo</label>
            <input type="file" ref={logoInputRef} accept="image/*" className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:bg-[#D4AF37]/20 file:text-[#D4AF37]" />
          </div>
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-xs font-medium text-gray-400 mb-3">BRANDING</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Primary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={formData.branding.primary_color} onChange={(e) => setFormData(p => ({ ...p, branding: { ...p.branding, primary_color: e.target.value } }))} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={formData.branding.primary_color} onChange={(e) => setFormData(p => ({ ...p, branding: { ...p.branding, primary_color: e.target.value } }))} className="bg-[#0B0E14] border-white/10 font-mono text-xs" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Secondary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={formData.branding.secondary_color} onChange={(e) => setFormData(p => ({ ...p, branding: { ...p.branding, secondary_color: e.target.value } }))} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={formData.branding.secondary_color} onChange={(e) => setFormData(p => ({ ...p, branding: { ...p.branding, secondary_color: e.target.value } }))} className="bg-[#0B0E14] border-white/10 font-mono text-xs" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#D4AF37] text-black" data-testid="tenant-submit">
              {submitting ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Create Tenant Admin Modal */}
      <Modal open={showAdminForm} onClose={() => setShowAdminForm(false)} title="Create Tenant Admin" maxWidth="max-w-md">
        <form onSubmit={handleCreateAdmin} className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Tenant</label>
            <select value={adminData.tenant_id} onChange={(e) => setAdminData(p => ({ ...p, tenant_id: e.target.value }))}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" required data-testid="admin-tenant-select">
              <option value="">Select tenant...</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} (/{t.slug})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Full Name</label>
            <Input value={adminData.full_name} onChange={(e) => setAdminData(p => ({ ...p, full_name: e.target.value }))} required className="bg-[#0B0E14] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Email</label>
            <Input type="email" value={adminData.email} onChange={(e) => setAdminData(p => ({ ...p, email: e.target.value }))} required className="bg-[#0B0E14] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Password</label>
            <Input type="password" value={adminData.password} onChange={(e) => setAdminData(p => ({ ...p, password: e.target.value }))} required className="bg-[#0B0E14] border-white/10" minLength={6} />
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
            <Button type="button" onClick={() => setShowAdminForm(false)} variant="outline" className="border-white/10">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#D4AF37] text-black" data-testid="create-admin-submit">{submitting ? 'Creating...' : 'Create Admin'}</Button>
          </div>
        </form>
      </Modal>

      {/* Change Slug Modal */}
      <Modal open={!!showSlugModal} onClose={() => setShowSlugModal(null)} title="Change Tenant URL" maxWidth="max-w-sm">
        <div className="space-y-4">
          <p className="text-xs text-gray-400">This will redirect all players from the old URL to the new one.</p>
          <div className="flex items-center bg-[#0B0E14] border border-white/10 rounded-lg overflow-hidden">
            <span className="px-3 py-2.5 text-xs text-gray-500 bg-[#0a0d12] border-r border-white/10">{PLATFORM_DOMAIN}/</span>
            <input value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
              className="flex-1 px-3 py-2.5 bg-transparent text-sm text-white font-mono outline-none" />
          </div>
          <div className="flex justify-end gap-3">
            <Button onClick={() => setShowSlugModal(null)} variant="outline" className="border-white/10">Cancel</Button>
            <Button onClick={handleSlugChange} className="bg-[#D4AF37] text-black">Update URL</Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal open={!!showResetPw} onClose={() => setShowResetPw(null)} title="Reset Admin Password" maxWidth="max-w-sm">
        <div className="space-y-4">
          <p className="text-xs text-gray-400">Reset password for <strong className="text-white">{showResetPw?.email}</strong></p>
          <div className="relative">
            <Input type={showPw ? 'text' : 'password'} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (min 6 chars)" className="bg-[#0B0E14] border-white/10 pr-10" minLength={6} />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex justify-end gap-3">
            <Button onClick={() => setShowResetPw(null)} variant="outline" className="border-white/10">Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetPw.length < 6} className="bg-[#D4AF37] text-black">Reset Password</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function AggCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[10px] text-gray-500">{label}</p>
      </div>
      <p className="text-sm font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

function MetricBox({ label, value, color }) {
  return (
    <div className="bg-[#0B0E14] rounded-lg p-2.5 text-center">
      <p className="text-sm font-bold" style={{ color }}>{value}</p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </div>
  );
}
